import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronRight, Loader2, Upload } from "lucide-react";
import { useSubmitManualCpdMutation } from "../../cpd/api/cpdApi";

/**
 * The candidate's CPD standing for this exam, and a way to top it up without
 * leaving the page.
 *
 * Modelled on the upgrade application's CPD gate: a member who is short is not
 * simply told to go away and come back. They can upload a certificate for CPD
 * earned outside the events list here, and those self-declared points count
 * TOWARDS the requirement immediately — the same "effective CPD" rule the
 * upgrade module uses — so the claim unblocks them while the secretariat works
 * through the certificates.
 *
 * That leniency is stated, not hidden: a claim that is later turned down takes
 * its points back out, and the panel says so wherever any claim is pending.
 */

export type CpdStanding = {
  /** Points already in the ledger. */
  total?: number;
  /** Points in claims submitted but not yet ruled on. */
  pending_points?: number;
  pending_count?: number;
  /** total + pending — what the gate is judged on. */
  effective?: number;
  required?: number | null;
  confirmed_at?: string | null;
  /** When the candidate declared they hold the points before paying. */
  attested_at?: string | null;
};

/** "60" rather than "60.00" — these are read, not calculated with. */
const fmt = (n: number) => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

export default function ExamCpdPanel({
  cpd, required, confirmed, onConfirmChange, onClaimed, disabled,
}: {
  cpd: CpdStanding;
  required: number;
  confirmed: boolean;
  onConfirmChange: (v: boolean) => void;
  /** Re-read the standing so a fresh claim counts straight away. */
  onClaimed: () => void;
  disabled?: boolean;
}) {
  const awarded = Number(cpd.total ?? 0);
  const pending = Number(cpd.pending_points ?? 0);
  const effective = Number(cpd.effective ?? 0);
  const attestedAt = cpd.attested_at ?? null;
  const short = effective < required;
  const shortfall = Math.max(0, required - effective);

  const pct = required > 0 ? Math.min(100, Math.round((effective / required) * 100)) : 0;

  return (
    <div
      className={`border rounded-xl p-4 ${
        short ? "border-amber-200 bg-amber-50/40" : "border-gray-100"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium text-gray-900 text-sm">CPD</div>
        <span
          className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${
            short
              ? "bg-amber-100 text-amber-800 border-amber-200"
              : "bg-emerald-100 text-emerald-800 border-emerald-200"
          }`}
        >
          {attestedAt ? "Declared" : short ? "Below minimum" : "Minimum met"}
        </span>
      </div>

      <p className="text-xs text-gray-500 mt-1">
        This exam requires {fmt(required)} CPD points.
      </p>

      <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${short ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        <Figure label="Required" value={fmt(required)} />
        <Figure label="Confirmed" value={fmt(awarded)} />
        <Figure label="In claims" value={fmt(pending)} />
        <Figure label="Counted" value={fmt(effective)} tone={short ? "bad" : "good"} />
      </div>

      {/* Said plainly rather than left to be inferred: unapproved points DO
          count here, and a rejected claim takes them back out. */}
      {pending > 0 ? (
        <p className="mt-3 text-xs text-gray-500">
          {fmt(pending)} point{pending === 1 ? "" : "s"} sit in {cpd.pending_count ?? 1} claim
          {(cpd.pending_count ?? 1) === 1 ? "" : "s"} awaiting approval. They count towards this
          now — if a claim is turned down they come back out and you would be short again.
        </p>
      ) : null}

      {/* A candidate who declared they hold the points before paying is not
          asked again. The declaration stands for this application, and the
          panel says so rather than nagging about a bar that no longer blocks
          them — but it keeps the claim form open, because getting the points
          onto the record is still the thing to do. */}
      {attestedAt ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-gray-600">
            You confirmed that you hold {fmt(required)} CPD points for this exam, and your
            application went through on that basis. Upload your certificates when you can — the
            secretariat may ask for them.
          </p>
          {!disabled ? <CpdClaimForm onClaimed={onClaimed} /> : null}
        </div>
      ) : short ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-start gap-2 text-xs text-amber-800">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              You are {fmt(shortfall)} point{shortfall === 1 ? "" : "s"} short and cannot submit
              yet. Upload a certificate for CPD you earned outside the events list — it counts
              straight away.
            </span>
          </div>
          {!disabled ? <CpdClaimForm onClaimed={onClaimed} /> : null}
        </div>
      ) : (
        <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            className="shrink-0 h-4 w-4 mt-0.5"
            checked={confirmed}
            disabled={disabled}
            onChange={(e) => onConfirmChange(e.target.checked)}
          />
          <span>
            I confirm that I hold at least {fmt(required)} CPD points as required for this exam.
          </span>
        </label>
      )}
    </div>
  );
}

/**
 * Claim CPD earned outside the events list.
 *
 * The same form and the same endpoint as the upgrade application's top-up, so a
 * member meets one flow whichever thing they are applying for, and a claim made
 * here shows up on their CPD page like any other.
 */
export function CpdClaimForm({ onClaimed }: { onClaimed: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [organizer, setOrganizer] = useState("");
  const [date, setDate] = useState("");
  const [points, setPoints] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [submit, { isLoading }] = useSubmitManualCpdMutation();

  const onSubmit = async () => {
    setMsg(null);
    const pts = Number(points);
    if (!title.trim() || !date || !file || isNaN(pts) || pts <= 0) {
      setMsg({
        ok: false,
        text: "Enter a title, date, CPD points (more than 0) and attach a certificate.",
      });
      return;
    }

    const fd = new FormData();
    fd.append("title", title.trim());
    if (organizer.trim()) fd.append("organizer", organizer.trim());
    fd.append("activity_date", date);
    fd.append("cpd_points_claimed", String(pts));
    fd.append("certificate", file);

    try {
      const res: any = await submit(fd).unwrap();
      setMsg({ ok: true, text: res?.message || "CPD certificate submitted." });
      setTitle(""); setOrganizer(""); setDate(""); setPoints(""); setFile(null);
      // Re-read the standing: the claimed points count from this moment, so the
      // panel — and the Submit button above it — must reflect that at once.
      onClaimed();
    } catch (err: any) {
      setMsg({ ok: false, text: err?.data?.message || "Upload failed. Please try again." });
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
      >
        <Upload className="w-3.5 h-3.5" /> Claim CPD to top up
      </button>
    );
  }

  return (
    <div className="p-3 rounded-lg bg-white border border-amber-200 space-y-2">
      <p className="text-[11px] text-gray-500">
        Upload a certificate for CPD you earned outside the events list and enter the points it
        carries. It counts towards this exam now and is confirmed on review.
      </p>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Activity / course title *"
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      <input
        type="text"
        value={organizer}
        onChange={(e) => setOrganizer(e.target.value)}
        placeholder="Organizer (optional)"
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      <div className="flex flex-wrap gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={new Date().toISOString().slice(0, 10)}
          className="flex-1 min-w-[9rem] border rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="number"
          min={0.5}
          step={0.5}
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          placeholder="Points *"
          className="w-28 border rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="w-full text-xs"
      />
      {msg && (
        <p className={`text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-rose-600"}`}>
          {msg.text}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {isLoading ? "Uploading…" : "Submit certificate"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
      <p className="text-[10px] text-gray-400">
        Or manage all your CPD claims on the{" "}
        <Link to="/my-cpd" className="underline">
          CPD page <ChevronRight className="w-3 h-3 inline" />
        </Link>
      </p>
    </div>
  );
}

function Figure({
  label, value, tone = "plain",
}: {
  label: string;
  value: string;
  tone?: "plain" | "good" | "bad";
}) {
  const tones = {
    plain: "bg-white border-gray-200 text-gray-900",
    good: "bg-emerald-50 border-emerald-100 text-emerald-800",
    bad: "bg-amber-50 border-amber-100 text-amber-800",
  } as const;

  return (
    <div className={`rounded-xl border p-2.5 ${tones[tone]}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</div>
      <div className="text-lg font-black tabular-nums">{value}</div>
    </div>
  );
}
